import { useState, useEffect, useRef } from 'react';

export const useAnalyticsData = (games, analyticsType = 'matchups') => {
  const [analyticsByGameId, setAnalyticsByGameId] = useState({});
  const [loadingGameIds, setLoadingGameIds] = useState(new Set());
  const fetchedGameIds = useRef(new Map()); // Track which games+types we've already fetched

  const fetchAnalyticsForGame = async (gameId) => {
    try {
      setLoadingGameIds(prev => new Set(prev).add(gameId));
      
      const endpoint = `/api/analytics/game/${gameId}/${analyticsType}`;
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`Raw analytics data for game ${gameId} (${analyticsType}):`, data);
      
      // Transform the data to create rows for the DataTable
      const analyticsRows = [];
      
      if (analyticsType === 'matchups') {
        // Handle matchups response format with year-based data
        console.log('Raw matchups data structure:', data);
        
        // New structure: data.matchups contains batters, each with matchups for pitchers
        // Each pitcher has a matchup object with years (2024, 2025, alltime)
        const battersArray = data.matchups || [];
        console.log('Batters array:', battersArray);
        
        // First, collect all available years from all matchups
        const availableYearsSet = new Set();
        
        if (Array.isArray(battersArray)) {
          battersArray.forEach(batter => {
            if (batter.matchups && typeof batter.matchups === 'object') {
              Object.values(batter.matchups).forEach(pitcherData => {
                if (pitcherData.matchup && typeof pitcherData.matchup === 'object') {
                  Object.keys(pitcherData.matchup).forEach(year => {
                    availableYearsSet.add(year);
                  });
                }
              });
            }
          });
        }
        
        const availableYears = Array.from(availableYearsSet).sort((a, b) => {
          // Sort with "alltime" first, then years in descending order
          if (a === 'alltime') return -1;
          if (b === 'alltime') return 1;
          return b.localeCompare(a);
        });
        
        console.log('Available years:', availableYears);
        
        // Store all years of data, organized by year
        const yearlyData = {};
        
        // Initialize each year
        availableYears.forEach(year => {
          yearlyData[year] = [];
        });
        
        // Process each batter and their matchups
        if (Array.isArray(battersArray)) {
          battersArray.forEach(batter => {
            if (batter.matchups && typeof batter.matchups === 'object') {
              Object.entries(batter.matchups).forEach(([pitcherId, pitcherData]) => {
                // For each year, create a row if data exists
                availableYears.forEach(year => {
                  const yearData = pitcherData.matchup?.[year];
                  if (yearData) {
                    yearlyData[year].push({
                      batterId: batter.batterId,
                      batterName: batter.batterName,
                      batterTeam: batter.batterTeam,
                      batterBats: batter.batterBats,
                      pitcherId: pitcherId,
                      pitcherName: pitcherData.pitcherName,
                      pitcherTeam: pitcherData.pitcherTeam,
                      pitcherThrows: pitcherData.pitcherThrows,
                      // Map the new field names to our expected format
                      totalAtBats: yearData.atBats || 0,
                      hits: yearData.hits || 0,
                      homers: yearData.homeRuns || 0,
                      strikeouts: yearData.strikeouts || 0,
                      walks: yearData.walks || 0,
                      battingAvg: yearData.battingAvg || 0,
                      obp: yearData.onBasePct || 0, // fallback if not provided
                      slg: yearData.sluggingPct || 0,
                      ops: yearData.ops || 0,
                      plateAppearances: yearData.plateAppearances || 0,
                      singles: yearData.singles || 0,
                      doubles: yearData.doubles || 0,
                      triples: yearData.triples || 0,
                      hitByPitch: yearData.hitByPitch || 0
                    });
                  }
                });
              });
            }
          });
        }
        
        console.log('Processed yearly data:', yearlyData);
        
        // Store the yearly data structure and available years
        setAnalyticsByGameId(prev => ({
          ...prev,
          [gameId]: {
            [analyticsType]: yearlyData,
            availableYears: availableYears,
            last_update: new Date().toISOString()
          }
        }));
        
        return; // Skip the regular analytics rows processing
      } else if (analyticsType === 'pitch') {
        // Handle pitch response format
        const pitchersArray = data.pitch || data;
        
        if (Array.isArray(pitchersArray)) {
          console.log(`Processing ${pitchersArray.length} pitch records`);
          pitchersArray.forEach(pitcher => {
            analyticsRows.push({
              pitcherId: pitcher.pitcherId,
              pitcherName: pitcher.pitcherName,
              pitcherTeam: pitcher.pitcherTeam,
              pitcherThrows: pitcher.pitcherThrows,
              // Pitch statistics
              totalPitches: pitcher.totalPitches || 0,
              strikes: pitcher.strikes || 0,
              balls: pitcher.balls || 0,
              strikePercentage: pitcher.strikePercentage || 0,
              avgVelocity: pitcher.avgVelocity || 0,
              maxVelocity: pitcher.maxVelocity || 0,
              era: pitcher.era || 0,
              whip: pitcher.whip || 0,
              kPer9: pitcher.kPer9 || 0,
              bbPer9: pitcher.bbPer9 || 0,
              // Add dummy batter fields for compatibility
              batterId: 'N/A',
              batterName: 'vs All',
              batterTeam: 'vs All',
              batterBats: 'N/A'
            });
          });
        }
      } else if (analyticsType === 'performance') {
        // Handle performance response format
        const battersArray = data.performance || data;
        
        if (Array.isArray(battersArray)) {
          console.log(`Processing ${battersArray.length} performance records`);
          battersArray.forEach(batter => {
            // For performance data, we'll include all three time periods
            // Data structure: batter.performance.last7Days.vsAll
            const last7 = batter.performance?.last7Days?.vsAll || {};
            const last14 = batter.performance?.last14Days?.vsAll || {};
            const last30 = batter.performance?.last30Days?.vsAll || {};
            
            // Debug log to see data structure
            if (battersArray.indexOf(batter) === 0) {
              console.log('First batter performance data:', {
                batterName: batter.batterName,
                performance: batter.performance,
                last7: last7,
                last14: last14,
                last30: last30
              });
            }
            
            analyticsRows.push({
              batterId: batter.batterId,
              batterName: batter.batterName,
              batterTeam: batter.batterTeam,
              batterBats: batter.batterBats,
              // Add dummy pitcher fields for compatibility with table component
              pitcherId: 'N/A',
              pitcherName: 'All Pitchers',
              pitcherThrows: 'N/A',
              // Last 7 days data
              last7_plateAppearances: last7.plateAppearances || 0,
              last7_totalAtBats: last7.atBats || 0,
              last7_hits: last7.hits || 0,
              last7_singles: last7.singles || 0,
              last7_doubles: last7.doubles || 0,
              last7_triples: last7.triples || 0,
              last7_homers: last7.homers || 0,
              last7_strikeouts: last7.strikeouts || 0,
              last7_walks: last7.walks || 0,
              last7_hitByPitch: last7.hitByPitch || 0,
              last7_sacFlies: last7.sacFlies || 0,
              last7_battingAvg: last7.battingAvg || 0,
              last7_obp: last7.onBasePct || 0,
              last7_slg: last7.sluggingPct || 0,
              last7_ops: last7.ops || 0,
              last7_strikeoutRate: last7.strikeoutRate || 0,
              last7_walkRate: last7.walkRate || 0,
              // Last 14 days data
              last14_plateAppearances: last14.plateAppearances || 0,
              last14_totalAtBats: last14.atBats || 0,
              last14_hits: last14.hits || 0,
              last14_singles: last14.singles || 0,
              last14_doubles: last14.doubles || 0,
              last14_triples: last14.triples || 0,
              last14_homers: last14.homers || 0,
              last14_strikeouts: last14.strikeouts || 0,
              last14_walks: last14.walks || 0,
              last14_hitByPitch: last14.hitByPitch || 0,
              last14_sacFlies: last14.sacFlies || 0,
              last14_battingAvg: last14.battingAvg || 0,
              last14_obp: last14.onBasePct || 0,
              last14_slg: last14.sluggingPct || 0,
              last14_ops: last14.ops || 0,
              last14_strikeoutRate: last14.strikeoutRate || 0,
              last14_walkRate: last14.walkRate || 0,
              // Last 30 days data
              last30_plateAppearances: last30.plateAppearances || 0,
              last30_totalAtBats: last30.atBats || 0,
              last30_hits: last30.hits || 0,
              last30_singles: last30.singles || 0,
              last30_doubles: last30.doubles || 0,
              last30_triples: last30.triples || 0,
              last30_homers: last30.homers || 0,
              last30_strikeouts: last30.strikeouts || 0,
              last30_walks: last30.walks || 0,
              last30_hitByPitch: last30.hitByPitch || 0,
              last30_sacFlies: last30.sacFlies || 0,
              last30_battingAvg: last30.battingAvg || 0,
              last30_obp: last30.onBasePct || 0,
              last30_slg: last30.sluggingPct || 0,
              last30_ops: last30.ops || 0,
              last30_strikeoutRate: last30.strikeoutRate || 0,
              last30_walkRate: last30.walkRate || 0
            });
          });
        }
      }
      
      console.log(`Transformed ${analyticsRows.length} ${analyticsType} rows for game ${gameId}`);
      
      setAnalyticsByGameId(prev => ({
        ...prev,
        [gameId]: {
          [analyticsType]: analyticsRows,
          last_update: new Date().toISOString()
        }
      }));
      
    } catch (err) {
      console.error(`Failed to fetch analytics for game ${gameId}:`, err);
      setAnalyticsByGameId(prev => ({
        ...prev,
        [gameId]: {
          [analyticsType]: [],
          error: err.message,
          last_update: new Date().toISOString()
        }
      }));
    } finally {
      setLoadingGameIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(gameId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    // Skip if games is null (not in analytics mode or NCAAF)
    if (!games) {
      return;
    }
    
    if (games.length > 0) {
      console.log(`Analytics: Checking which games need ${analyticsType} data fetch`);
      
      // Only fetch analytics for games+type combinations we haven't fetched yet
      games.forEach(game => {
        const gameId = game.gameIDmlb;
        const cacheKey = `${gameId}-${analyticsType}`;
        
        // Check if we already have this specific analytics type data for this game
        const hasData = analyticsByGameId[gameId]?.[analyticsType];
        
        if (!fetchedGameIds.current.has(cacheKey) || !hasData) {
          console.log(`Analytics: Fetching ${analyticsType} data for game ${gameId}`);
          fetchedGameIds.current.set(cacheKey, true);
          fetchAnalyticsForGame(gameId);
        } else {
          console.log(`Analytics: Skipping game ${gameId} ${analyticsType} - already fetched`);
        }
      });
    } else if (games.length === 0) {
      // Clear the fetched IDs when games array is empty (mode switch)
      console.log('Analytics: Clearing fetched game IDs');
      fetchedGameIds.current.clear();
      setAnalyticsByGameId({});
    }
  }, [games, analyticsType]); // Add analyticsType to dependencies

  return { 
    analyticsByGameId, 
    loadingGameIds
  };
};